import { getPropertyUnitTenancyHistory } from '../api/services/propertyService';

/** Flatten common API shapes so tenant name/id match the UI. */
export function normalizeUnitFromApi(unit) {
  if (!unit || typeof unit !== 'object') return unit;
  const nested = unit.tenant || unit.assignedUser || unit.user;
  let tenantName = unit.tenantName;
  let tenantId = unit.tenantId;

  let tenantUserId = unit.tenantUserId || unit.assignedUserId || unit.userId || null;

  if (nested && typeof nested === 'object') {
    if (tenantName == null || String(tenantName).trim() === '') {
      tenantName =
        nested.fullName ||
        nested.name ||
        [nested.firstName, nested.lastName].filter(Boolean).join(' ').trim() ||
        nested.email ||
        null;
    }
    if (tenantId == null || tenantId === '') {
      const nestedId = nested.id ?? nested.userId ?? nested.tenantId;
      tenantId = nestedId != null ? nestedId : tenantId;
    }
    if (!tenantUserId) {
      tenantUserId = nested.id || nested.userId || null;
    }
  }

  if (tenantName != null) {
    tenantName = String(tenantName).trim() || null;
  }
  if (tenantId != null && tenantId !== '') {
    tenantId = String(tenantId);
  }

  return { ...unit, tenantName, tenantId, tenantUserId };
}

function unitHasAssignedTenant(unit) {
  if (!unit) return false;
  const name = unit.tenantName != null ? String(unit.tenantName).trim() : '';
  const id = unit.tenantId != null ? String(unit.tenantId).trim() : '';
  return Boolean(name || id);
}

/**
 * Merge ACTIVE tenancy records into units so occupied/vacant counts match Property Details.
 * Only fetches history for units that do not already have tenant info on the payload.
 */
export async function hydrateUnitsWithActiveTenancy(propertyId, units = []) {
  const baseUnits = (units || []).map(normalizeUnitFromApi);
  if (!propertyId || baseUnits.length === 0) return baseUnits;

  const unitsNeedingHydration = baseUnits.filter((u) => u?.id && !unitHasAssignedTenant(u));
  if (unitsNeedingHydration.length === 0) return baseUnits;

  const resolved = await Promise.allSettled(
    unitsNeedingHydration.map(async (unit) => {
      const historyResponse = await getPropertyUnitTenancyHistory(propertyId, unit.id);
      const historyRows = Array.isArray(historyResponse?.data) ? historyResponse.data : [];
      const activeRows = historyRows.filter(
        (row) => String(row?.status || '').toUpperCase() === 'ACTIVE'
      );
      if (activeRows.length === 0) return null;

      const latestActiveRow = [...activeRows].sort((a, b) => {
        const aTs = new Date(a?.createdAt || a?.startDate || 0).getTime();
        const bTs = new Date(b?.createdAt || b?.startDate || 0).getTime();
        return bTs - aTs;
      })[0];

      const fullName = `${latestActiveRow?.userFirstName || ''} ${latestActiveRow?.userLastName || ''}`.trim();
      return {
        unitId: unit.id,
        tenantName:
          fullName ||
          latestActiveRow?.userEmail ||
          latestActiveRow?.tenantCode ||
          'Tenant assigned',
        tenantId: latestActiveRow?.tenantCode || latestActiveRow?.userId || unit.tenantId || null,
      };
    })
  );

  const activeByUnitId = new Map();
  resolved.forEach((result) => {
    if (result.status !== 'fulfilled' || !result.value?.unitId) return;
    activeByUnitId.set(result.value.unitId, result.value);
  });

  return baseUnits.map((unit) => {
    const activeTenant = activeByUnitId.get(unit.id);
    if (!activeTenant) return unit;
    return {
      ...unit,
      tenantName: activeTenant.tenantName,
      tenantId: activeTenant.tenantId,
      status: unit.status === 'damaged' ? 'damaged' : 'occupied',
    };
  });
}
